import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { formatCardNumber } from '@/lib/utils/billing'
import { CardData, CardField } from '@/lib/checkout/card'

interface CardPaymentFieldsProps {
  value: CardData
  /** i18n keys (namespace `checkout`) per field. */
  errors: Partial<Record<CardField, string | undefined>>
  onChange: (field: CardField, value: string) => void
  /** Installment count offered; 1 hides the selector. */
  maxInstallments?: number
  installments?: number
  onInstallmentsChange?: (installments: number) => void
  /** How each installment option reads, e.g. "12× de R$ 99,00"; defaults to "12x". */
  installmentLabel?: (count: number) => string
  disabled?: boolean
  className?: string
}

const formatExpiry = (raw: string): string => {
  const d = raw.replace(/\D/g, '').slice(0, 4)
  return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d
}

/**
 * The card, as typed. The number and CVV never reach the API: the purchase
 * flow exchanges them for a provider token at submit time.
 */
export function CardPaymentFields({
  value,
  errors,
  onChange,
  maxInstallments = 1,
  installments = 1,
  onInstallmentsChange,
  installmentLabel,
  disabled,
  className
}: CardPaymentFieldsProps) {
  const { t } = useTranslation('checkout')

  const label = (name: string, text: string) => (
    <label htmlFor={`checkout-card-${name}`} className="mb-1 block text-xs text-gray-500">
      {text}
    </label>
  )
  const error = (name: CardField) =>
    errors[name] ? (
      <p id={`checkout-card-${name}-error`} className="mt-1 text-xs text-red-600">
        {t(errors[name] as string)}
      </p>
    ) : null
  const inputProps = (name: CardField) => ({
    id: `checkout-card-${name}`,
    'aria-invalid': Boolean(errors[name]),
    'aria-describedby': errors[name] ? `checkout-card-${name}-error` : undefined,
    disabled,
    className: cn('h-11 bg-white', errors[name] && 'border-red-500')
  })

  return (
    <div className={cn('space-y-3', className)}>
      <div>
        {label('number', t('purchase.fields.cardNumber'))}
        <Input
          {...inputProps('number')}
          inputMode="numeric"
          autoComplete="cc-number"
          value={formatCardNumber(value.number)}
          onChange={(e) => onChange('number', e.target.value.replace(/\D/g, '').slice(0, 19))}
        />
        {error('number')}
      </div>
      <div>
        {label('holderName', t('purchase.fields.cardHolder'))}
        <Input
          {...inputProps('holderName')}
          autoComplete="cc-name"
          value={value.holderName}
          onChange={(e) => onChange('holderName', e.target.value.toUpperCase())}
        />
        {error('holderName')}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="min-w-0">
          {label('expiry', t('purchase.fields.cardExpiry'))}
          <Input
            {...inputProps('expiry')}
            inputMode="numeric"
            autoComplete="cc-exp"
            placeholder={t('purchase.placeholders.cardExpiry')}
            value={value.expiry}
            onChange={(e) => onChange('expiry', formatExpiry(e.target.value))}
          />
          {error('expiry')}
        </div>
        <div className="min-w-0">
          {label('cvv', t('purchase.fields.cardCvv'))}
          <Input
            {...inputProps('cvv')}
            inputMode="numeric"
            autoComplete="cc-csc"
            value={value.cvv}
            onChange={(e) => onChange('cvv', e.target.value.replace(/\D/g, '').slice(0, 4))}
          />
          {error('cvv')}
        </div>
      </div>
      {maxInstallments > 1 && onInstallmentsChange && (
        <div>
          {label('installments', t('purchase.fields.installments'))}
          <select
            id="checkout-card-installments"
            value={installments}
            onChange={(e) => onInstallmentsChange(Number(e.target.value))}
            disabled={disabled}
            className="h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
          >
            {Array.from({ length: maxInstallments }, (_, i) => i + 1).map((count) => (
              <option key={count} value={count}>
                {installmentLabel ? installmentLabel(count) : t('purchase.installmentOption', { count })}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  )
}
